
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Alert } from "@/types/ticker";

export function useAlerts(kitId: string | null) {
  const queryClient = useQueryClient();

  const { data: alerts = [] } = useQuery({
    queryKey: ['alerts', kitId],
    queryFn: async () => {
      if (!kitId) return [];
      const { data, error } = await supabase
        .from('alerts')
        .select('*')
        .eq('kit_id', kitId)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      return data as Alert[];
    },
    enabled: !!kitId,
  });

  const addAlertMutation = useMutation({
    mutationFn: async (text: string) => {
      if (!kitId) throw new Error("No kit selected");
      const { error } = await supabase
        .from('alerts')
        .insert([{ kit_id: kitId, text, is_active: true }]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', kitId] });
    },
  });

  const updateAlertMutation = useMutation({
    mutationFn: async ({ id, text }: { id: string; text: string }) => {
      const { error } = await supabase
        .from('alerts')
        .update({ text })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', kitId] });
    },
  });

  const toggleAlertActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('alerts')
        .update({ is_active: isActive })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', kitId] });
    },
  });

  const removeAlertMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('alerts')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', kitId] });
    },
  });

  return {
    alerts,
    addAlertMutation,
    updateAlertMutation,
    toggleAlertActiveMutation,
    removeAlertMutation,
  };
}
